import type { FieldPath, FieldValues, PathValue } from 'react-hook-form';
import type { Func } from '@ricoh/ria.client.core/types/core/function.type';

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isStrongPassword } from '@ricoh/ria.client.core/utils/secure/password';

import type { PasswordValidationRules } from './_password.type';

/**
 * Use PasswordValidationRules.
 *
 * @template TFieldValues FieldValues object.
 * @template TFieldName Field name.
 * @returns {PasswordValidationRules<TFieldValues, TFieldName>} PasswordValidationRules.
 */
export function usePasswordValidationRules<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(): PasswordValidationRules<TFieldValues, TFieldName> {
  const { t } = useTranslation();

  const isValidPassword = useCallback<Func<[fieldName: string, value: string], true | string>>(
    (fieldName, value) => {
      // Min length 12
      if (value.length < 12) {
        return t(`validation:password.length`, { fieldName });
      }

      // Require digit
      if (!/\d/.test(value)) {
        return t(`validation:password.digits`, { fieldName });
      }

      // Require uppercase
      if (!/[A-Z]/.test(value)) {
        return t(`validation:password.uppercase`, { fieldName });
      }

      // Require lowercase
      if (!/[a-z]/.test(value)) {
        return t(`validation:password.lowercase`, { fieldName });
      }

      // Require non alphabetic
      if (!/[^a-zA-Z\d\s]/.test(value)) {
        return t(`validation:password.nonAlphabetic`, { fieldName });
      }

      return true;
    },
    [t],
  );

  const isStrong = useCallback<Func<[fieldName: string, value: string], true | string>>(
    (fieldName, value) => {
      // Verify strong password
      if (!isStrongPassword(value)) {
        return t(`validation:password.strongPassword`, { fieldName });
      }

      return true;
    },
    [t],
  );

  const validPasswordFormat = useCallback<Func<[value: PathValue<TFieldValues, TFieldName>, patternItems: string[]], boolean>>(
    (value, patternItems) => patternItems.some((item) => item && value.toLowerCase().includes(item.toLowerCase())),
    [],
  );

  return useMemo<PasswordValidationRules<TFieldValues, TFieldName>>(
    () => ({
      password: (fieldName) => ({
        validPassword: (value) => isValidPassword(fieldName, value),
        strongPassword: (value) => isStrong(fieldName, value),
      }),

      newPassword: (fieldName, passwordFieldName, fieldSelector) => ({
        validNewPassword: (value, formValues) => {
          const { email, password, name = '' } = fieldSelector(formValues);
          const isValidPasswordFormat = validPasswordFormat(value, [email, ...name.split(' ')]);
          if (isValidPasswordFormat) {
            return t(`validation:password.inValidFormat`, { fieldName });
          }

          if (value === password) {
            t(`validation:password.notEqualsPassword`, {
              fieldName,
              passwordFieldName,
            });
          }

          return true;
        },
      }),

      confirmPassword: (fieldName, newPasswordSelector) => ({
        equalsPassword: (value, formValues) => {
          const password = newPasswordSelector(formValues);
          return value === password ? true : t(`validation:password.equalsPassword`, { fieldName });
        },
      }),
    }),
    [isStrong, isValidPassword, t, validPasswordFormat],
  );
}
